// closeReportExcelHelper.js
//
// Builds the "SALIM template" workbook (CASH SUMMARY, STOCK REPORT, CREDIT,
// DELIVERIES, CASH DISCOUNT DETAILS, ORDERS) from a close report's data -
// same sheet names/order/sections as SALIM_TEMPLATE_NEW_1.xlsx, filled with
// real data instead of the template's placeholder rows.
//
// Smart-skip: ORDERS is left out of the generated file when it has no rows
// for this period. CASH SUMMARY, CREDIT and CASH DISCOUNT DETAILS follow
// ORDERS rather than being checked purely on their own:
//   - ORDERS is empty -> all three are included regardless, even if they
//     are empty too (nothing from orders to be selective about).
//   - ORDERS has rows -> each of the three is only included if IT has
//     data; an empty one among them is left out.
// STOCK REPORT and DELIVERIES are always included either way. See
// buildCloseReportTemplateWorkbook. This only affects what gets exported
// into the Excel file - it never removes or changes anything in the
// underlying close report, closing form, or Orders records.
//
// ORDERS is populated from the cloud's /sales_by_range endpoint (order sales
// for this report's own start_date/end_date window) - see fetchOrderSales /
// buildOrdersSheet below. Filling it needs a network call, so
// buildCloseReportTemplateWorkbook/downloadCloseReportTemplate are async and
// take an auth token as their third argument.
//
// That endpoint used to live in order_report.py, which also carried the
// Orders page's manual period reporting. That feature was removed; the
// endpoint survives on its own in order_sales_range.py at the SAME URL, so
// nothing here changed. This is now the only consumer of it, which is worth
// knowing before anyone deletes it as unused.
//
// Source data:
//   report.pumps_list, report.tanks_list, report.products_list  (existing)
//   report.station_serial, report.start_date, report.end_date   (for ORDERS)
//   report.closing_form = {
//     pump_test:     [{ product, quantity }],
//     cash_discount: [{ customer, plate_number, company, product, quantity, discount_price }],
//     cash_expenses: [{ particulars, amount }],
//     collections:   [{ provider, reference, amount }],
//     credit:        [{ driver_name, plate_number, company, product, quantity, order_number }],
//     fuel_expenses: [{ particulars, category, department, product, quantity }],
//     totals: {
//       pump_test_by_product, cash_discount_by_product,
//       credit_by_product, fuel_expenses_by_product,
//       cash_expenses_amount, collections_amount,
//     },
//   }
//
// Usage (from CloseReports.js or anywhere else):
//   import { downloadCloseReportTemplate } from './closeReportExcelHelper';
//   await downloadCloseReportTemplate(report, stationName, date, accessToken);
//
// Per request, every cell (headers AND data) is bold + centered, and every
// sheet opens with the station name + report period so each tab is
// self-explanatory on its own.

import * as XLSX from 'xlsx-js-style';

import { API_BASE } from '../config';

// ─── Style constants ──────────────────────────────────────────────────────────
const THIN_BORDER = {
  top:    { style: 'thin' },
  bottom: { style: 'thin' },
  left:   { style: 'thin' },
  right:  { style: 'thin' },
};
// Used on grand-total / final-summary rows to visually separate them from
// the data rows just above, in addition to their green fill.
const TOTAL_BORDER = {
  top:    { style: 'medium' },
  bottom: { style: 'thin' },
  left:   { style: 'thin' },
  right:  { style: 'thin' },
};
const CENTER      = { horizontal: 'center', vertical: 'center' };
const arial       = (sz, bold = true) => ({ name: 'Arial', sz, bold });
const TITLE_FONT   = arial(13, true);
const GROUP_FONT   = arial(11, true);
const HEADER_FONT  = arial(10, true);
const DATA_FONT    = arial(10, true); // bold, per request - applies to every data cell too
const SECTION_FILL = { patternType: 'solid', fgColor: { rgb: '1F3864' } }; // dark navy - top-level section bars
const GROUP_FILL   = { patternType: 'solid', fgColor: { rgb: '4472C4' } }; // medium blue - PETROL/DIESEL subgroup bars
const HEADER_FILL  = { patternType: 'solid', fgColor: { rgb: 'D9E2F3' } }; // light blue - column header rows
const TOTAL_FILL   = { patternType: 'solid', fgColor: { rgb: 'C6EFCE' } }; // green - totals
const GRAND_FILL   = { patternType: 'solid', fgColor: { rgb: 'FFE699' } }; // amber - grand totals / final balances
const INPUT_FILL   = { patternType: 'solid', fgColor: { rgb: 'FFF2CC' } }; // manual-entry cells (no source data)
const BREAKDOWN_FILL = { patternType: 'solid', fgColor: { rgb: 'F2F2F2' } }; // light grey - secondary breakdown rows (e.g. CREDIT/CASH/PUMP TEST under a meter table)
const WHITE_FONT   = { name: 'Arial', sz: 12, bold: true, color: { rgb: 'FFFFFF' } };
const GROUP_WHITE_FONT = { name: 'Arial', sz: 11, bold: true, color: { rgb: 'FFFFFF' } };

// ─── Sheet helpers (same pattern as CloseReports.js) ──────────────────────────
function expandRange(ws, r, c) {
  if (!ws['!ref']) {
    ws['!ref'] = XLSX.utils.encode_range({ s: { r, c }, e: { r, c } });
    return;
  }
  const range = XLSX.utils.decode_range(ws['!ref']);
  if (r < range.s.r) range.s.r = r;
  if (c < range.s.c) range.s.c = c;
  if (r > range.e.r) range.e.r = r;
  if (c > range.e.c) range.e.c = c;
  ws['!ref'] = XLSX.utils.encode_range(range);
}

function dc(ws, r, c, value, opts = {}) {
  const v = value === null || value === undefined ? '' : value;
  const cell = { v };
  cell.t = typeof v === 'number' ? 'n' : 's';
  cell.s = {
    font:      opts.font || DATA_FONT,
    alignment: CENTER,
    border:    opts.border || THIN_BORDER,
  };
  if (opts.fmt)  cell.z      = opts.fmt;
  if (opts.fill) cell.s.fill = opts.fill;
  ws[XLSX.utils.encode_cell({ r, c })] = cell;
  expandRange(ws, r, c);
}

function setFormula(ws, r, c, formula, opts = {}) {
  // opts.value: the result, stored with the formula so it shows even before
  // the spreadsheet recalculates.
  const cell = { f: formula, t: 'n', v: opts.value != null ? opts.value : 0 };
  cell.s = {
    font:      opts.font || DATA_FONT,
    alignment: CENTER,
    border:    opts.border || THIN_BORDER,
  };
  if (opts.fmt)  cell.z      = opts.fmt;
  if (opts.fill) cell.s.fill = opts.fill;
  ws[XLSX.utils.encode_cell({ r, c })] = cell;
  expandRange(ws, r, c);
}

function mergeCells(ws, r1, c1, r2, c2) {
  if (!ws['!merges']) ws['!merges'] = [];
  ws['!merges'].push({ s: { r: r1, c: c1 }, e: { r: r2, c: c2 } });
}

function setColWidths(ws, widths) {
  ws['!cols'] = widths.map((w) => ({ wch: w }));
}

function setRowHeight(ws, r, pts) {
  if (!ws['!rows']) ws['!rows'] = [];
  ws['!rows'][r] = { hpt: pts };
}

// Top-level section bar (dark navy, white text) - e.g. "DISCOUNT BREAKDOWN".
function sectionTitle(ws, r, c1, c2, text) {
  mergeCells(ws, r, c1, r, c2);
  dc(ws, r, c1, text, { font: WHITE_FONT, fill: SECTION_FILL });
  for (let c = c1 + 1; c <= c2; c++) dc(ws, r, c, '', { fill: SECTION_FILL });
  setRowHeight(ws, r, 22);
}

// Second-level subgroup bar (medium blue, white text) - e.g. "PETROL"/"DIESEL",
// visually nested one step below a sectionTitle bar.
function groupTitle(ws, r, c1, c2, text) {
  mergeCells(ws, r, c1, r, c2);
  dc(ws, r, c1, text, { font: GROUP_WHITE_FONT, fill: GROUP_FILL });
  for (let c = c1 + 1; c <= c2; c++) dc(ws, r, c, '', { fill: GROUP_FILL });
  setRowHeight(ws, r, 19);
}

// Station name + report period, written at the top of every sheet so each
// tab reads clearly on its own (matches what STOCK REPORT already had).
// Returns the row index the sheet's own content should start at.
function addReportHeader(ws, lastCol, report, stationName) {
  let r = 0;
  mergeCells(ws, r, 0, r, lastCol);
  dc(ws, r, 0, (stationName || '').toUpperCase(), { font: TITLE_FONT });
  setRowHeight(ws, r, 24);
  r += 1;

  mergeCells(ws, r, 0, r, lastCol);
  const period = `${(report.start_date || '').slice(0, 16).replace('T', ' ')} to ${(report.end_date || '').slice(0, 16).replace('T', ' ')}`;
  dc(ws, r, 0, period, { font: GROUP_FONT });
  setRowHeight(ws, r, 16);
  r += 2;

  return r;
}

// Long text (expense particulars, customer names, labels) used to be cut off
// at the column edge. Run on every finished sheet:
//   1. each column is widened to its longest text, up to FIT_MAX_WIDTH
//   2. text still longer than its column wraps onto more lines, and that row
//      is made tall enough to show all of it
// Titles merged across several columns (station name, period, section bars)
// are skipped - they are meant to span, not to stretch one column.
// Widths only ever grow; a column is never made narrower than it was set.
const FIT_MAX_WIDTH = 45;    // characters
const CHAR_WIDTH    = 1.15;  // bold Arial is wider than Excel's default font
const LINE_HEIGHT   = 14;    // points per wrapped line

function fitLongText(ws) {
  if (!ws || !ws['!ref']) return ws;
  const range = XLSX.utils.decode_range(ws['!ref']);

  const merged = new Set();
  (ws['!merges'] || []).forEach((m) => {
    for (let r = m.s.r; r <= m.e.r; r++) {
      for (let c = m.s.c; c <= m.e.c; c++) merged.add(`${r}:${c}`);
    }
  });
  const textOf = (r, c) => {
    if (merged.has(`${r}:${c}`)) return '';
    const cell = ws[XLSX.utils.encode_cell({ r, c })];
    return cell && cell.t === 's' && typeof cell.v === 'string' ? cell.v : '';
  };

  const cols = ws['!cols'] || [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    let longest = 0;
    for (let r = range.s.r; r <= range.e.r; r++) longest = Math.max(longest, textOf(r, c).length);
    const current = (cols[c] && cols[c].wch) || 10;
    const wanted  = Math.min(Math.ceil(longest * CHAR_WIDTH) + 2, FIT_MAX_WIDTH);
    cols[c] = { wch: Math.max(current, wanted) };
  }
  ws['!cols'] = cols;

  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const text = textOf(r, c);
      if (!text) continue;
      const perLine = Math.max(1, Math.floor((cols[c].wch - 2) / CHAR_WIDTH));
      if (text.length <= perLine) continue;
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      cell.s = { ...(cell.s || {}), alignment: { ...((cell.s && cell.s.alignment) || {}), wrapText: true } };
      const lines = Math.ceil(text.length / perLine);
      if (!ws['!rows']) ws['!rows'] = [];
      const currentHeight = (ws['!rows'][r] && ws['!rows'][r].hpt) || 15;
      ws['!rows'][r] = { hpt: Math.max(currentHeight, lines * LINE_HEIGHT + 4) };
    }
  }
  return ws;
}

// ─── Shared lookups ────────────────────────────────────────────────────────────
const DIESEL_NAMES   = new Set(['DIESEL', 'AGO', 'GASOIL']);
const UNLEADED_NAMES = new Set(['UNLEADED', 'PETROL', 'PMS', 'GASOLINE']);

function productGroup(productName) {
  const p = (productName || '').toUpperCase();
  if (DIESEL_NAMES.has(p))   return 'DIESEL';
  if (UNLEADED_NAMES.has(p)) return 'PETROL';
  return null;
}

// All tanks of one product group. A station can have more than one tank of
// the same product (e.g. two Diesel tanks), so anything that used to take
// "the" tank with tanks.find() must use every tank and add them up -
// find() only ever returned the first one.
function tanksOf(tanks, group) {
  return (tanks || []).filter((t) => productGroup(t.TankProdName) === group);
}

function sumTanks(groupTanks, field) {
  return groupTanks.reduce((sum, t) => sum + (Number(t[field]) || 0), 0);
}

function buildPriceMap(report) {
  const map = {};
  (report.products_list || []).forEach((p) => {
    const group = productGroup(p.product);
    if (group) map[group] = p.Price || 0;
  });
  return map;
}

// Price used to value one closing-form row. A daily report's rows have no
// price of their own and use the report's pump price, exactly as before.
// The multi-day custom report (custom_close_report.js) sets `pump_price` on
// every row to the price of the day that row came from, because the pump
// price can change in the middle of a multi-day period.
function priceOf(row, priceMap) {
  return row && row.pump_price != null ? row.pump_price : (priceMap[productGroup(row && row.product)] || 0);
}

// Same idea for a pump row: the custom report gives each pump the period's
// weighted-average price for its product; a daily pump row has none.
function pumpPriceOf(pump, priceMap) {
  return pump && pump.pump_price != null ? pump.pump_price : (priceMap[productGroup(pump && pump.product)] || 0);
}

// True when `arr` has at least one row - used to decide whether a sheet
// gets included in the workbook at all (see buildCloseReportTemplateWorkbook).
function hasRows(arr) {
  return Array.isArray(arr) && arr.length > 0;
}

function emptyClosingForm() {
  return {
    opening_cash: 0,
    to_be_deposited: 0,
    pump_test: [], cash_discount: [], cash_expenses: [],
    collections: [], credit: [], fuel_expenses: [],
    totals: {
      pump_test_by_product: {}, cash_discount_by_product: {},
      credit_by_product: {}, fuel_expenses_by_product: {},
      cash_expenses_amount: 0, collections_amount: 0,
    },
  };
}

// Extracts just the time portion ("10:34:12") out of a "YYYY-MM-DD HH:MM:SS"
// style string. Falls back to the original string if it doesn't look like
// "date time" (e.g. it's already time-only, or empty).
function extractTime(dateTimeStr) {
  if (!dateTimeStr) return '';
  const str = String(dateTimeStr).trim();
  const parts = str.split(' ');
  return parts.length > 1 ? parts[1] : str;
}

// Money GIVEN AWAY in discounts = sum of qty * (pump price - discount price).
//
// ! This changed with the meaning of discount_price. That field is the price
// the manager actually charged (e.g. 3,500 against a 4,086 pump price), NOT
// the reduction. So the amount forgone is the GAP between the two, and
// `qty * discount_price` — what this used to return — is the money the
// station COLLECTED at the discounted rate. Subtracting that on CASH
// SUMMARY's "LESS DISCOUNTS" line would deduct the whole discounted sale
// instead of the discount, which on a large fuel sale is an enormous error
// in the wrong direction.
//
// Computed here rather than referenced across sheets because the
// discount-details table's row count (and so its TOTAL DISCOUNT cell) moves
// with the data; there is no fixed cell to point at. Same reasoning as
// totalCreditAmount.
function totalDiscountAmount(closingForm, priceMap) {
  return (closingForm.cash_discount || []).reduce((sum, row) => {
    const pumpPrice = priceOf(row, priceMap);
    // Not clamped at zero on purpose: a discount_price ABOVE the pump price
    // is a data-entry mistake, and letting it show as a negative discount
    // makes it visible instead of silently rounding it away.
    return sum + (row.quantity || 0) * (pumpPrice - (row.discount_price || 0));
  }, 0);
}

// Total credit amount = sum(qty * pump price) across every credit row.
// Same reasoning as totalDiscountAmount - computed directly rather than
// via a cross-sheet reference into CREDIT's (variable-position) totals.
function totalCreditAmount(closingForm, priceMap) {
  return (closingForm.credit || []).reduce(
    (sum, row) => sum + (row.quantity || 0) * priceOf(row, priceMap), 0
  );
}

// Money value of everything the pumps put out this period = sum over pumps of
// (litres sold * that product's pump price). Deliberately the same arithmetic
// STOCK REPORT's per-group AMOUNT totals use, off the same pumps_list, so the
// two sheets cannot disagree.
//
// Computed here rather than referenced across sheets because STOCK REPORT's
// total rows move with the number of pumps - there is no fixed cell to point
// at. Same reasoning as totalDiscountAmount / totalCreditAmount.
function totalFuelDispensedAmount(report, priceMap) {
  return (report.pumps_list || []).reduce((sum, p) => {
    return sum + (p.total_volume_by_sale || 0) * pumpPriceOf(p, priceMap);
  }, 0);
}

// Money value of fuel run through the pumps for testing: litres never sold,
// so it comes off the cash the station is expected to hold.
//
// ! The quantity keys here are UNLEADED / DIESEL, while priceMap is keyed
// PETROL / DIESEL. Those two vocabularies are not interchangeable and the
// mapping has to be explicit - indexing priceMap with 'UNLEADED' silently
// yields undefined, which would make every petrol pump test worth nothing.
// STOCK REPORT does the same translation; see its creditQty/pumpTestQty.
function totalPumpTestAmount(closingForm, priceMap) {
  // Multi-day custom report: already valued day by day, at each day's price.
  if (closingForm.totals && typeof closingForm.totals.pump_test_amount === 'number') {
    return closingForm.totals.pump_test_amount;
  }
  const byProduct = (closingForm.totals && closingForm.totals.pump_test_by_product) || {};
  return (byProduct.UNLEADED || 0) * (priceMap.PETROL || 0)
       + (byProduct.DIESEL   || 0) * (priceMap.DIESEL || 0);
}

// ─── CASH SUMMARY sheet ────────────────────────────────────────────────────────
// COLLECTION, CASH EXPENSES, FUEL EXPENSES tables + a SUMMARY box.
const CASH_SUMMARY_LAST_COL = 7; // H

function buildCashSummarySheet(report, stationName, closingForm, priceMap) {
  const ws = {};
  setColWidths(ws, [6, 26, 18, 16, 16, 16, 16, 16]);
  let r = addReportHeader(ws, CASH_SUMMARY_LAST_COL, report, stationName);

  // ── COLLECTION ──────────────────────────────────────────────────────────
  sectionTitle(ws, r, 0, 2, 'COLLECTION');
  r += 1;
  dc(ws, r, 0, 'SN',               { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 1, 'PROVIDER / REF',   { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 2, 'AMOUNT',           { font: HEADER_FONT, fill: HEADER_FILL });
  setRowHeight(ws, r, 18);
  r += 1;

  const collectionStart = r;
  closingForm.collections.forEach((row, i) => {
    dc(ws, r, 0, i + 1);
    dc(ws, r, 1, row.reference ? `${row.provider || ''} (${row.reference})` : (row.provider || ''));
    dc(ws, r, 2, row.amount || 0, { fmt: '#,##0' });
    r += 1;
  });
  const collectionEnd = r - 1;

  const collectionTotalRow = r;
  dc(ws, r, 1, 'TOTAL', { fill: TOTAL_FILL });
  if (collectionStart <= collectionEnd) {
    setFormula(ws, r, 2, `SUM(C${collectionStart + 1}:C${collectionEnd + 1})`, { fmt: '#,##0', fill: TOTAL_FILL });
  } else {
    dc(ws, r, 2, 0, { fmt: '#,##0', fill: TOTAL_FILL });
  }
  r += 2;

  // ── CASH EXPENSES ───────────────────────────────────────────────────────
  sectionTitle(ws, r, 0, 2, 'CASH EXPENSES');
  r += 1;
  dc(ws, r, 0, 'SN',          { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 1, 'PARTICULARS', { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 2, 'AMOUNT',      { font: HEADER_FONT, fill: HEADER_FILL });
  setRowHeight(ws, r, 18);
  r += 1;

  const cashExpStart = r;
  closingForm.cash_expenses.forEach((row, i) => {
    dc(ws, r, 0, i + 1);
    dc(ws, r, 1, row.particulars || '');
    dc(ws, r, 2, row.amount || 0, { fmt: '#,##0' });
    r += 1;
  });
  const cashExpEnd = r - 1;

  const cashExpTotalRow = r;
  dc(ws, r, 1, 'TOTAL', { fill: TOTAL_FILL });
  if (cashExpStart <= cashExpEnd) {
    setFormula(ws, r, 2, `SUM(C${cashExpStart + 1}:C${cashExpEnd + 1})`, { fmt: '#,##0', fill: TOTAL_FILL });
  } else {
    dc(ws, r, 2, 0, { fmt: '#,##0', fill: TOTAL_FILL });
  }
  r += 2;

  // ── FUEL EXPENSES ───────────────────────────────────────────────────────
  sectionTitle(ws, r, 0, 7, 'FUEL EXPENSES');
  r += 1;
  dc(ws, r, 0, 'SN',          { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 1, 'PARTICULARS', { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 2, 'CATEGORY',    { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 3, 'DEPARTMENT',  { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 4, 'PRODUCT',     { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 5, 'LTRS',        { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 6, 'PRICE',       { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 7, 'AMOUNT',      { font: HEADER_FONT, fill: HEADER_FILL });
  setRowHeight(ws, r, 18);
  r += 1;

  const fuelExpStart = r;
  closingForm.fuel_expenses.forEach((row, i) => {
    const price = priceOf(row, priceMap);
    dc(ws, r, 0, i + 1);
    dc(ws, r, 1, row.particulars || '');
    dc(ws, r, 2, row.category    || '');
    dc(ws, r, 3, row.department  || '');
    dc(ws, r, 4, row.product     || '');
    dc(ws, r, 5, row.quantity    || 0, { fmt: '#,##0.00' });
    dc(ws, r, 6, price,                { fmt: '#,##0' });
    setFormula(ws, r, 7, `F${r + 1}*G${r + 1}`, { fmt: '#,##0' });
    r += 1;
  });
  const fuelExpEnd = r - 1;

  const fuelExpTotalRow = r;
  // Label sits right next to the AMOUNT total it describes (col 6, PRICE)
  // instead of under DEPARTMENT, so it's visually obvious what the 0/total
  // in col 7 belongs to.
  dc(ws, r, 6, 'TOTAL', { fill: TOTAL_FILL });
  if (fuelExpStart <= fuelExpEnd) {
    setFormula(ws, r, 7, `SUM(H${fuelExpStart + 1}:H${fuelExpEnd + 1})`, { fmt: '#,##0', fill: TOTAL_FILL });
  } else {
    dc(ws, r, 7, 0, { fmt: '#,##0', fill: TOTAL_FILL });
  }
  r += 2;

  // ── SUMMARY ─────────────────────────────────────────────────────────────
  // Laid out as an accounting statement rather than a flat list:
  //
  //     OPENING AMOUNT
  //     ADD      TOTAL FUEL DISPENSED AMOUNT
  //     LESS     PUMP TEST / EXPENSES / DISCOUNTS / CREDITS / COLLECTIONS
  //     =        TO BE DEPOSITED
  //     LESS     DEPOSITED
  //     =        CASH CLOSING BALANCE
  //
  // ! COLLECTIONS IS SUBTRACTED HERE. The previous layout ADDED it. This is
  // a change of meaning, not just of position: a "collection" is money taken
  // for fuel by some route OTHER than cash in the till (mobile money and the
  // like), so it is fuel that was dispensed but whose value the operator is
  // not holding. Counting it as an addition inflated TO BE DEPOSITED by
  // twice the collected amount — once inside TOTAL FUEL DISPENSED, once
  // again on its own line. Any station using mobile money will see its TO BE
  // DEPOSITED drop against previously exported reports; that drop is the bug
  // being fixed, not a new one.
  //
  // Labels go in column B (wide), values in column C — NOT column A, which
  // is intentionally narrow (SN-width) and would clip long label text.
  sectionTitle(ws, r, 0, 2, 'SUMMARY');
  r += 1;

  dc(ws, r, 1, 'OPENING AMOUNT', { fill: HEADER_FILL });
  dc(ws, r, 2, closingForm.opening_cash || 0, { fmt: '#,##0' });
  const openingRow = r;
  r += 2;   // deliberate blank row — separates the opening balance from ADD

  // ── ADD ───────────────────────────────────────────────────────────────
  dc(ws, r, 1, 'ADD', { fill: HEADER_FILL });
  r += 1;

  dc(ws, r, 1, 'TOTAL FUEL DISPENSED AMOUNT', { fill: HEADER_FILL });
  dc(ws, r, 2, totalFuelDispensedAmount(report, priceMap), { fmt: '#,##0' });
  const dispensedRow = r;
  r += 2;   // deliberate blank row

  // ── LESS ──────────────────────────────────────────────────────────────
  dc(ws, r, 1, 'LESS', { fill: HEADER_FILL });
  r += 1;

  // Every deduction carries a real computed figure, including the ones that
  // come out at zero. A blank cell and a zero are indistinguishable to a
  // reader but not to a reviewer: blank reads as "nobody filled this in",
  // zero reads as "checked, nothing there". The formula below treats them
  // the same either way, so showing the zero costs nothing.
  dc(ws, r, 1, 'PUMP TEST', { fill: HEADER_FILL });
  dc(ws, r, 2, totalPumpTestAmount(closingForm, priceMap), { fmt: '#,##0' });
  const pumpTestRow = r;
  r += 1;

  dc(ws, r, 1, 'EXPENSES', { fill: HEADER_FILL });
  setFormula(ws, r, 2, `C${cashExpTotalRow + 1}+H${fuelExpTotalRow + 1}`, { fmt: '#,##0' });
  const expensesRow = r;
  r += 1;

  dc(ws, r, 1, 'DISCOUNTS', { fill: HEADER_FILL });
  dc(ws, r, 2, totalDiscountAmount(closingForm, priceMap), { fmt: '#,##0' });
  const discountRow = r;
  r += 1;

  dc(ws, r, 1, 'CREDITS', { fill: HEADER_FILL });
  dc(ws, r, 2, totalCreditAmount(closingForm, priceMap), { fmt: '#,##0' });
  const creditRow = r;
  r += 1;

  dc(ws, r, 1, 'COLLECTIONS', { fill: HEADER_FILL });
  setFormula(ws, r, 2, `C${collectionTotalRow + 1}`, { fmt: '#,##0' });
  const collectionsRow = r;
  r += 1;

  // ── TO BE DEPOSITED ───────────────────────────────────────────────────
  // A live formula, not a stored figure, so anyone auditing the sheet can
  // follow it straight back to the five rows above and the two tables those
  // rows reference.
  dc(ws, r, 1, 'TO BE DEPOSITED', { fill: GRAND_FILL, border: TOTAL_BORDER });
  setFormula(
    ws, r, 2,
    `C${openingRow + 1}+C${dispensedRow + 1}`
    + `-C${pumpTestRow + 1}-C${expensesRow + 1}-C${discountRow + 1}`
    + `-C${creditRow + 1}-C${collectionsRow + 1}`,
    { fmt: '#,##0', fill: GRAND_FILL, border: TOTAL_BORDER }
  );
  const toBeDepositedRow = r;
  r += 1;

  // What the operator actually handed over, confirmed on the closing
  // wizard's "Review and confirm" step — not a manual cell.
  dc(ws, r, 1, 'DEPOSITED', { fill: HEADER_FILL });
  dc(ws, r, 2, closingForm.to_be_deposited || 0, { fmt: '#,##0' });
  const depositedRow = r;
  r += 1;

  // Anything other than zero here is a shortage or an overage.
  dc(ws, r, 1, 'CASH CLOSING BALANCE', { fill: GRAND_FILL, border: TOTAL_BORDER });
  setFormula(ws, r, 2, `C${toBeDepositedRow + 1}-C${depositedRow + 1}`, { fmt: '#,##0', fill: GRAND_FILL, border: TOTAL_BORDER });
  r += 1;

  return ws;
}

// ─── CREDIT sheet (PETROL + DIESEL) ────────────────────────────────────────────
const CREDIT_LAST_COL = 7; // H

function buildCreditSheet(report, stationName, closingForm, priceMap) {
  const ws = {};
  setColWidths(ws, [6, 20, 16, 18, 14, 14, 16, 16]);
  let r = addReportHeader(ws, CREDIT_LAST_COL, report, stationName);

  const groups = [
    { label: 'PETROL', rows: closingForm.credit.filter((c) => productGroup(c.product) === 'PETROL') },
    { label: 'DIESEL', rows: closingForm.credit.filter((c) => productGroup(c.product) === 'DIESEL') },
  ];

  sectionTitle(ws, r, 0, 7, 'CREDIT BREAKDOWN');
  r += 1;

  groups.forEach((group) => {
    groupTitle(ws, r, 0, 7, group.label);
    r += 1;

    dc(ws, r, 0, 'SN',           { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 1, 'DRIVER NAME',  { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 2, 'PLATE NUMBER', { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 3, 'COMPANY',      { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 4, 'QTY',          { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 5, 'PRICE',        { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 6, 'AMOUNT',       { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 7, 'ORDER NUMBER', { font: HEADER_FONT, fill: HEADER_FILL });
    setRowHeight(ws, r, 18);
    r += 1;

    const dataStart = r;
    group.rows.forEach((row, i) => {
      const price = priceOf(row, priceMap);
      dc(ws, r, 0, i + 1);
      dc(ws, r, 1, row.driver_name  || '');
      dc(ws, r, 2, row.plate_number || '');
      dc(ws, r, 3, row.company      || '');
      dc(ws, r, 4, row.quantity     || 0, { fmt: '#,##0.00' });
      dc(ws, r, 5, price,                 { fmt: '#,##0' });
      setFormula(ws, r, 6, `E${r + 1}*F${r + 1}`, { fmt: '#,##0' });
      dc(ws, r, 7, row.order_number || '');
      r += 1;
    });
    const dataEnd = r - 1;

    dc(ws, r, 3, 'TOTAL', { fill: TOTAL_FILL });
    if (dataStart <= dataEnd) {
      setFormula(ws, r, 4, `SUM(E${dataStart + 1}:E${dataEnd + 1})`, { fmt: '#,##0.00', fill: TOTAL_FILL });
      setFormula(ws, r, 6, `SUM(G${dataStart + 1}:G${dataEnd + 1})`, { fmt: '#,##0', fill: TOTAL_FILL });
    } else {
      dc(ws, r, 4, 0, { fmt: '#,##0.00', fill: TOTAL_FILL });
      dc(ws, r, 6, 0, { fmt: '#,##0', fill: TOTAL_FILL });
    }
    r += 2;
  });

  return ws;
}

// ─── DELIVERIES sheet (PETROL + DIESEL) ────────────────────────────────────────
// Source: report.tanks_list refill fields.
//   DriverName          -> DRIVER NAME
//   TankerPlate         -> PLATE NUMBER
//   AutorefillIssueDate -> TIME (time-only portion, e.g. "10:34:12")
//   ATGDeliveryVolume   -> ATG QTY
//   OrderQty            -> QTY
//   SupplierName        -> SUPPLIER
//   VARIATION           = ATG RECEIVED - EXPECTED QTY (minus = less arrived
//                         than ordered); blank when either is missing
// ORDER NUMBER has no source field yet - left blank on purpose (per
// instruction: kept in the sheet, to be wired up later).
const DELIVERIES_LAST_COL = 8; // I

function buildDeliveriesSheet(report, stationName) {
  const ws = {};
  setColWidths(ws, [6, 18, 16, 18, 12, 12, 14, 16, 16]);
  let r = addReportHeader(ws, DELIVERIES_LAST_COL, report, stationName);

  sectionTitle(ws, r, 0, DELIVERIES_LAST_COL, 'FUEL RECEIVED');
  r += 1;

  const tanks = report.tanks_list || [];
  // Every tank of the product, not just the first one - a station with two
  // Diesel tanks lists the deliveries of both.
  const groups = [
    { label: 'PETROL', tanks: tanksOf(tanks, 'PETROL') },
    { label: 'DIESEL', tanks: tanksOf(tanks, 'DIESEL') },
  ];

  groups.forEach((group) => {
    groupTitle(ws, r, 0, DELIVERIES_LAST_COL, group.label);
    r += 1;

    dc(ws, r, 0, 'SN',           { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 1, 'DRIVER NAME',  { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 2, 'PLATE NUMBER', { font: HEADER_FONT, fill: HEADER_FILL });
    // Multi-day custom report: every delivery of the period, with its date.
    const multiDay = group.tanks.some((t) => Array.isArray(t.deliveries));
    dc(ws, r, 3, multiDay ? 'DATE / TIME' : 'TIME', { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 4, 'EXPECTED QTY (L)', { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 5, 'ATG RECEIVED (L)', { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 6, 'VARIATION (L)', { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 7, 'SUPPLIER',     { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 8, 'ORDER NUMBER', { font: HEADER_FONT, fill: HEADER_FILL });
    setRowHeight(ws, r, 18);
    r += 1;

    const hasDelivery = (t) => t.TankerPlate || t.DriverName || t.SupplierName || (t.OrderQty || 0) > 0 || (t.ATGDeliveryVolume || 0) > 0;
    const deliveryRows = [];
    group.tanks.forEach((t) => {
      if (Array.isArray(t.deliveries)) {
        deliveryRows.push(...t.deliveries);
      } else if (hasDelivery(t)) {
        deliveryRows.push({
          driver: t.DriverName, plate: t.TankerPlate, time: extractTime(t.AutorefillIssueDate),
          qty: t.OrderQty, atgQty: t.ATGDeliveryVolume, supplier: t.SupplierName,
        });
      }
    });
    const dataStart = r;
    let variationTotal = 0;
    deliveryRows.forEach((d, i) => {
      const qty = Number(d.qty) || 0;
      const atg = Number(d.atgQty) || 0;
      dc(ws, r, 0, i + 1);
      dc(ws, r, 1, d.driver   || '');
      dc(ws, r, 2, d.plate    || '');
      dc(ws, r, 3, d.time     || '');
      dc(ws, r, 4, qty, { fmt: '#,##0.00' });
      dc(ws, r, 5, atg, { fmt: '#,##0.00' });
      if (qty > 0 && atg > 0) {
        const variation = Math.round((atg - qty) * 100) / 100;
        variationTotal += variation;
        setFormula(ws, r, 6, `F${r + 1}-E${r + 1}`, { fmt: VAR_FMT, font: varFont(variation), value: variation });
      } else {
        dc(ws, r, 6, '');
      }
      dc(ws, r, 7, d.supplier || '');
      dc(ws, r, 8, '' /* order number - no source field yet, left blank */);
      r += 1;
    });
    const dataEnd = r - 1;
    variationTotal = Math.round(variationTotal * 100) / 100;

    // Label sits right next to QTY (col 4), the first of the two totals,
    // instead of under DRIVER NAME several columns away.
    dc(ws, r, 3, 'TOTAL', { fill: TOTAL_FILL });
    if (dataStart <= dataEnd) {
      setFormula(ws, r, 4, `SUM(E${dataStart + 1}:E${dataEnd + 1})`, { fmt: '#,##0.00', fill: TOTAL_FILL });
      setFormula(ws, r, 5, `SUM(F${dataStart + 1}:F${dataEnd + 1})`, { fmt: '#,##0.00', fill: TOTAL_FILL });
      setFormula(ws, r, 6, `SUM(G${dataStart + 1}:G${dataEnd + 1})`,
        { fmt: VAR_FMT, fill: TOTAL_FILL, font: varFont(variationTotal), value: variationTotal });
    } else {
      dc(ws, r, 4, 0, { fmt: '#,##0.00', fill: TOTAL_FILL });
      dc(ws, r, 5, 0, { fmt: '#,##0.00', fill: TOTAL_FILL });
      dc(ws, r, 6, 0, { fmt: VAR_FMT, fill: TOTAL_FILL });
    }
    r += 2;
  });

  return ws;
}

// ─── CASH DISCOUNT DETAILS sheet (PETROL + DIESEL) ─────────────────────────────
// discount_price is the price the MANAGER ENTERED — the rate actually
// charged — and is shown exactly as entered. It is not a reduction to be
// taken off the pump price. Example: pump price 4,086, manager enters
// 3,500 -> "DISCOUNT PRICE" shows 3,500, and the discount given away is the
// 586 gap, which the TOTALS block at the bottom derives.
//
// This previously displayed `pumpPrice - discount_price`, reading the field
// as the reduction. If any older report was built on that reading, its
// DISCOUNT PRICE column will not match a freshly exported one.
//
// There is no "Order Number" field on cash_discount rows - that column is
// left blank (manual).
const CASH_DISCOUNT_LAST_COL = 9; // J

function buildCashDiscountSheet(report, stationName, closingForm, priceMap) {
  const ws = {};
  // G/H/I/J need real headroom, not just enough to match the header text
  // length: every cell in the header row is filled (no blank neighbor to
  // overflow into), so a column sized to the exact character count gets
  // truncated outright - e.g. "DISCOUNT PRICE AMOUNT" (22 chars) in an
  // 18-wide column renders as "SCOUNT PRICE AMOUNT".
  setColWidths(ws, [6, 20, 16, 18, 12, 14, 20, 16, 26, 16]);
  let r = addReportHeader(ws, CASH_DISCOUNT_LAST_COL, report, stationName);

  sectionTitle(ws, r, 0, 9, 'DISCOUNT BREAKDOWN');
  r += 1;

  const groups = [
    { label: 'PETROL', rows: closingForm.cash_discount.filter((c) => productGroup(c.product) === 'PETROL') },
    { label: 'DIESEL', rows: closingForm.cash_discount.filter((c) => productGroup(c.product) === 'DIESEL') },
  ];

  const totalRows = [];

  groups.forEach((group) => {
    groupTitle(ws, r, 0, 9, group.label);
    r += 1;

    dc(ws, r, 0, 'SN',                     { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 1, 'CUSTOMER',               { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 2, 'PLATE NUMBER',           { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 3, 'COMPANY',                { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 4, 'QTY',                    { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 5, 'PUMP PRICE',             { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 6, 'PUMP PRICE AMOUNT',      { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 7, 'DISCOUNT PRICE',         { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 8, 'DISCOUNT PRICE AMOUNT',  { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 9, 'ORDER NUMBER',           { font: HEADER_FONT, fill: HEADER_FILL });
    setRowHeight(ws, r, 18);
    r += 1;

    const dataStart = r;
    group.rows.forEach((row, i) => {
      const pumpPrice = priceOf(row, priceMap);
      dc(ws, r, 0, i + 1);
      dc(ws, r, 1, row.customer     || '');
      dc(ws, r, 2, row.plate_number || '');
      dc(ws, r, 3, row.company      || '');
      dc(ws, r, 4, row.quantity     || 0, { fmt: '#,##0.00' });
      dc(ws, r, 5, pumpPrice,              { fmt: '#,##0' });
      setFormula(ws, r, 6, `E${r + 1}*F${r + 1}`, { fmt: '#,##0' });
      // Exactly what the manager entered — no arithmetic applied.
      dc(ws, r, 7, row.discount_price || 0, { fmt: '#,##0' });
      setFormula(ws, r, 8, `E${r + 1}*H${r + 1}`, { fmt: '#,##0' });
      dc(ws, r, 9, '' /* no order number field on cash_discount */);
      r += 1;
    });
    const dataEnd = r - 1;

    const totalRow = r;
    dc(ws, r, 3, 'TOTAL', { fill: TOTAL_FILL });
    if (dataStart <= dataEnd) {
      setFormula(ws, r, 4, `SUM(E${dataStart + 1}:E${dataEnd + 1})`, { fmt: '#,##0.00', fill: TOTAL_FILL });
      setFormula(ws, r, 6, `SUM(G${dataStart + 1}:G${dataEnd + 1})`, { fmt: '#,##0', fill: TOTAL_FILL });
      setFormula(ws, r, 8, `SUM(I${dataStart + 1}:I${dataEnd + 1})`, { fmt: '#,##0', fill: TOTAL_FILL });
    } else {
      dc(ws, r, 4, 0, { fmt: '#,##0.00', fill: TOTAL_FILL });
      dc(ws, r, 6, 0, { fmt: '#,##0', fill: TOTAL_FILL });
      dc(ws, r, 8, 0, { fmt: '#,##0', fill: TOTAL_FILL });
    }
    totalRows.push(totalRow);
    r += 2;
  });

  // Grand totals, boxed under their own dark-navy bar - same treatment as
  // CASH SUMMARY's "SUMMARY" section, so the sheet's final figures read as
  // a clearly separated box rather than three loose rows after the tables.
  sectionTitle(ws, r, 0, 9, 'TOTALS');
  r += 1;

  // Labels here are merged across B:C - "TOTAL PUMP PRICE AMOUNT SOLD" (29
  // chars) and "TOTAL DISCOUNT PRICE SOLD" (26 chars) are both wider than
  // column B alone, and a plain overflow spills the tail of the text past
  // the fill into the blank C cell, leaving it half-coloured/half-white.
  // Merging gives the fill a cell to actually cover.
  mergeCells(ws, r, 1, r, 2);
  dc(ws, r, 1, 'TOTAL PUMP PRICE AMOUNT SOLD', { fill: HEADER_FILL });
  dc(ws, r, 2, '', { fill: HEADER_FILL });
  setFormula(ws, r, 3, `G${totalRows[0] + 1}+G${totalRows[1] + 1}`, { fmt: '#,##0', border: TOTAL_BORDER });
  const grandPumpRow = r;
  r += 1;

  mergeCells(ws, r, 1, r, 2);
  dc(ws, r, 1, 'TOTAL DISCOUNT PRICE SOLD', { fill: HEADER_FILL });
  dc(ws, r, 2, '', { fill: HEADER_FILL });
  setFormula(ws, r, 3, `I${totalRows[0] + 1}+I${totalRows[1] + 1}`, { fmt: '#,##0', border: TOTAL_BORDER });
  const grandDiscountRow = r;
  r += 1;

  mergeCells(ws, r, 1, r, 2);
  dc(ws, r, 1, 'TOTAL DISCOUNT', { fill: GRAND_FILL, border: TOTAL_BORDER });
  dc(ws, r, 2, '', { fill: GRAND_FILL, border: TOTAL_BORDER });
  setFormula(ws, r, 3, `D${grandPumpRow + 1}-D${grandDiscountRow + 1}`, { fmt: '#,##0', fill: GRAND_FILL, border: TOTAL_BORDER });
  // This cell is what CASH SUMMARY's DISCOUNT TOTAL row references
  // (kept in sync with the row offsets below in buildCloseReportTemplateWorkbook).
  r += 1;

  return ws;
}

// ─── STOCK REPORT sheet ─────────────────────────────────────────────────────────
// Per-pump meter readings + per-product CREDIT/CASH/PUMP TEST breakdown +
// stock movement (opening/receipts/pump test/dispensed/closing/ATG/variation).
// Confirmed: uses BOTH pumps_list (meter readings) and tanks_list (stock
// movement) - keep this data scope, this rebuild only fixes the layout.
//
// Column plan: A = SN (narrow), B = PUMP/PRODUCT name (wide enough for
// labels like "OPENING STOCK"), C.. = metric columns. The meter table and
// the stock-movement table below it reuse the same column grid with
// different headers per section (same approach as the original template).
const STOCK_REPORT_LAST_COL = 8; // I

function buildStockReportSheet(report, stationName, closingForm, priceMap) {
  const ws = {};
  setColWidths(ws, [6, 18, 16, 16, 15, 15, 16, 16, 16]);
  let r = addReportHeader(ws, STOCK_REPORT_LAST_COL, report, stationName);

  const pumps = report.pumps_list || [];
  const tanks = report.tanks_list || [];

  // Top-level bar, matching the dark-navy "<X> BREAKDOWN" bar every other
  // sheet opens its content with, before the PETROL/DIESEL subgroups.
  sectionTitle(ws, r, 0, 8, 'STOCK BREAKDOWN');
  r += 1;

  ['PETROL', 'DIESEL'].forEach((group) => {
    groupTitle(ws, r, 0, 8, group);
    r += 1;

    // ── Per-pump meter table ──────────────────────────────────────────
    dc(ws, r, 0, 'SN',             { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 1, 'PUMP',           { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 2, 'OPENING METER',  { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 3, 'CLOSING METER',  { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 4, 'LTRS',           { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 5, 'PRICE',          { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 6, 'AMOUNT',         { font: HEADER_FONT, fill: HEADER_FILL });
    setRowHeight(ws, r, 18);
    r += 1;

    const groupPumps = pumps.filter((p) => productGroup(p.product) === group);
    const pumpStart = r;
    groupPumps.forEach((p, i) => {
      const price = pumpPriceOf(p, priceMap);
      dc(ws, r, 0, i + 1);
      dc(ws, r, 1, p.pump || '');
      dc(ws, r, 2, p.electronic_totalizer      || 0, { fmt: '#,##0.00' });
      dc(ws, r, 3, p.last_electronic_totalizer || 0, { fmt: '#,##0.00' });
      dc(ws, r, 4, p.total_volume_by_sale      || 0, { fmt: '#,##0.00' });
      dc(ws, r, 5, price,                              { fmt: '#,##0' });
      setFormula(ws, r, 6, `E${r + 1}*F${r + 1}`, { fmt: '#,##0' });
      r += 1;
    });
    const pumpEnd = r - 1;

    const ltrsTotalRow = r;
    dc(ws, r, 3, 'TOTAL', { fill: TOTAL_FILL });
    if (pumpStart <= pumpEnd) {
      setFormula(ws, r, 4, `SUM(E${pumpStart + 1}:E${pumpEnd + 1})`, { fmt: '#,##0.00', fill: TOTAL_FILL });
      setFormula(ws, r, 6, `SUM(G${pumpStart + 1}:G${pumpEnd + 1})`, { fmt: '#,##0', fill: TOTAL_FILL });
    } else {
      dc(ws, r, 4, 0, { fmt: '#,##0.00', fill: TOTAL_FILL });
      dc(ws, r, 6, 0, { fmt: '#,##0', fill: TOTAL_FILL });
    }
    r += 1;

    // ── Breakdown: CREDIT / CASH / PUMP TEST, from closing_form.totals ──
    // Light-grey fill across the whole row sets this 3-row breakdown apart
    // from the raw per-pump data above it, instead of blending into it.
    const creditQty   = (closingForm.totals.credit_by_product   || {})[group === 'PETROL' ? 'UNLEADED' : 'DIESEL'] || 0;
    const pumpTestQty = (closingForm.totals.pump_test_by_product || {})[group === 'PETROL' ? 'UNLEADED' : 'DIESEL'] || 0;

    dc(ws, r, 1, 'CREDIT', { fill: BREAKDOWN_FILL });
    dc(ws, r, 2, '', { fill: BREAKDOWN_FILL });
    dc(ws, r, 3, '', { fill: BREAKDOWN_FILL });
    dc(ws, r, 4, creditQty, { fmt: '#,##0.00', fill: BREAKDOWN_FILL });
    dc(ws, r, 5, '', { fill: BREAKDOWN_FILL });
    setFormula(ws, r, 6, `E${r + 1}*F${pumpStart + 1}`, { fmt: '#,##0', fill: BREAKDOWN_FILL });
    const creditRow = r;
    r += 1;

    dc(ws, r, 1, 'CASH', { fill: BREAKDOWN_FILL });
    dc(ws, r, 2, '', { fill: BREAKDOWN_FILL });
    dc(ws, r, 3, '', { fill: BREAKDOWN_FILL });
    setFormula(ws, r, 4, `E${ltrsTotalRow + 1}-E${creditRow + 1}`, { fmt: '#,##0.00', fill: BREAKDOWN_FILL });
    dc(ws, r, 5, '', { fill: BREAKDOWN_FILL });
    setFormula(ws, r, 6, `E${r + 1}*F${pumpStart + 1}`, { fmt: '#,##0', fill: BREAKDOWN_FILL });
    r += 1;

    dc(ws, r, 1, 'PUMP TEST', { fill: BREAKDOWN_FILL });
    dc(ws, r, 2, '', { fill: BREAKDOWN_FILL });
    dc(ws, r, 3, '', { fill: BREAKDOWN_FILL });
    dc(ws, r, 4, pumpTestQty, { fmt: '#,##0.00', fill: BREAKDOWN_FILL });
    dc(ws, r, 5, '', { fill: BREAKDOWN_FILL });
    setFormula(ws, r, 6, `E${r + 1}*F${pumpStart + 1}`, { fmt: '#,##0', fill: BREAKDOWN_FILL });
    const pumpTestRow = r;
    r += 1;

    // Group total - restates the meter table's TOTAL (same qty/amount) as
    // one clear bottom-line row right under the breakdown, bold/bordered
    // so it reads as this product's final figure before moving on.
    dc(ws, r, 3, `${group} TOTAL`, { fill: GRAND_FILL, border: TOTAL_BORDER });
    setFormula(ws, r, 4, `E${ltrsTotalRow + 1}`, { fmt: '#,##0.00', fill: GRAND_FILL, border: TOTAL_BORDER });
    dc(ws, r, 5, '', { fill: GRAND_FILL, border: TOTAL_BORDER });
    setFormula(ws, r, 6, `G${ltrsTotalRow + 1}`, { fmt: '#,##0', fill: GRAND_FILL, border: TOTAL_BORDER });
    r += 2;

    // ── Stock movement (from tanks_list) ─────────────────────────────
    // Starts at column B, not A - column A is kept narrow (SN-width, 6)
    // everywhere else on this sheet, and both the "OPENING STOCK" label
    // and values like StartVolume (e.g. "1,549.00") don't fit in it,
    // which is what caused the clipped/"#####" cells here before.
    // Every tank of the product (a station can have two Diesel tanks).
    const groupTanks = tanksOf(tanks, group);
    const multiTank  = groupTanks.length > 1;
    dc(ws, r, 1, 'OPENING STOCK',  { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 2, 'RECEIVED',       { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 3, 'PUMP TEST',      { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 4, 'DISPENSED',      { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 5, 'CLOSING STOCK',  { font: HEADER_FONT, fill: HEADER_FILL });
    dc(ws, r, 6, 'ATG', { font: HEADER_FONT, fill: HEADER_FILL });
    // DAY VARIATION is the key diagnostic figure on this sheet (the
    // discrepancy between book stock and the physical ATG reading), so it
    // gets the same amber "final figure" treatment as other sheets' bottom
    // lines instead of blending in as just another column.
    dc(ws, r, 7, 'DAY VARIATION',  { font: HEADER_FONT, fill: GRAND_FILL });
    setRowHeight(ws, r, 18);
    r += 1;

    const stockRow = r;
    // Per-tank lines (only when the product has more than one tank) go
    // right under this row: a small header, then one line per tank. The
    // product row's OPENING / RECEIVED / ATG are SUMs of those lines, so
    // the totals stay traceable to each tank.
    const tankHeaderRow = stockRow + 1;
    const tankFirstRow  = stockRow + 2;
    const tankLastRow   = tankFirstRow + groupTanks.length - 1;
    const sumCol = (col) => `SUM(${col}${tankFirstRow + 1}:${col}${tankLastRow + 1})`;

    if (multiTank) {
      setFormula(ws, r, 1, sumCol('B'), { fmt: '#,##0.00' });
    } else {
      dc(ws, r, 1, sumTanks(groupTanks, 'StartVolume'), { fmt: '#,##0.00' });
    }
    // RECEIPTS = the delivery QTY (OrderQty - same value as QTY on the
    // DELIVERIES sheet), not the ATG-measured delivery. Changes CLOSING
    // STOCK and DAY VARIATION too, since both are calculated from it.
    if (multiTank) {
      setFormula(ws, r, 2, sumCol('C'), { fmt: '#,##0.00' });
    } else {
      dc(ws, r, 2, sumTanks(groupTanks, 'OrderQty'), { fmt: '#,##0.00' });
    }
    setFormula(ws, r, 3, `E${pumpTestRow + 1}`, { fmt: '#,##0.00' });
    setFormula(ws, r, 4, `E${ltrsTotalRow + 1}`, { fmt: '#,##0.00' });
    setFormula(ws, r, 5, `B${stockRow + 1}+C${stockRow + 1}-E${stockRow + 1}+D${stockRow + 1}`, { fmt: '#,##0.00' });
    if (multiTank) {
      setFormula(ws, r, 6, sumCol('G'), { fmt: '#,##0.00' });
    } else {
      dc(ws, r, 6, sumTanks(groupTanks, 'EndVolume'), { fmt: '#,##0.00' });
    }
    setFormula(ws, r, 7, `G${stockRow + 1}-F${stockRow + 1}`, { fmt: '#,##0.00', fill: GRAND_FILL, border: TOTAL_BORDER });
    r += 1;

    if (multiTank) {
      r = tankHeaderRow;
      dc(ws, r, 0, 'TANK',          { font: HEADER_FONT, fill: BREAKDOWN_FILL });
      dc(ws, r, 1, 'OPENING STOCK', { font: HEADER_FONT, fill: BREAKDOWN_FILL });
      dc(ws, r, 2, 'RECEIVED',      { font: HEADER_FONT, fill: BREAKDOWN_FILL });
      dc(ws, r, 6, 'ATG',           { font: HEADER_FONT, fill: BREAKDOWN_FILL });
      r += 1;
      groupTanks.forEach((t) => {
        dc(ws, r, 0, t.TankID != null ? t.TankID : '', { fill: BREAKDOWN_FILL });
        dc(ws, r, 1, Number(t.StartVolume) || 0, { fmt: '#,##0.00', fill: BREAKDOWN_FILL });
        dc(ws, r, 2, Number(t.OrderQty)    || 0, { fmt: '#,##0.00', fill: BREAKDOWN_FILL });
        dc(ws, r, 6, Number(t.EndVolume)   || 0, { fmt: '#,##0.00', fill: BREAKDOWN_FILL });
        r += 1;
      });
    }
    r += 1;
  });

  return ws;
}

// ─── ORDERS sheet ───────────────────────────────────────────────────────────
// One row per order sale (SalesReport.card_id LIKE 'ORD-%') within the close
// report's own start_date/end_date window - fetched from /sales_by_range
// (see fetchOrderSales below).
//
// Reads SALES, not order cards. That distinction matters now that an order
// voucher is destroyed by its first use and auto-deleted 24 hours after
// creation if never used: the fuel_card row is long gone by the time most
// close reports are exported, but the sale it produced stays in
// sales_reports forever. So this sheet is unaffected by either rule - it
// reports what was actually dispensed, which is the only thing a close
// report cares about.
//
// Columns: Order ID, Date, Car Plate, TIN Number, Customer Name, Product,
// Pump, Volume, Amount.
const ORDERS_LAST_COL = 8; // I

function buildOrdersSheet(report, stationName, orderRows) {
  const ws = {};
  setColWidths(ws, [20, 18, 14, 16, 20, 12, 12, 10, 12]);
  let r = addReportHeader(ws, ORDERS_LAST_COL, report, stationName);

  sectionTitle(ws, r, 0, 8, 'ORDERS');
  r += 1;

  dc(ws, r, 0, 'ORDER ID',      { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 1, 'DATE',          { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 2, 'CAR PLATE',     { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 3, 'TIN NUMBER',    { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 4, 'CUSTOMER NAME', { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 5, 'PRODUCT',       { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 6, 'PUMP',          { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 7, 'VOLUME',        { font: HEADER_FONT, fill: HEADER_FILL });
  dc(ws, r, 8, 'AMOUNT',        { font: HEADER_FONT, fill: HEADER_FILL });
  setRowHeight(ws, r, 18);
  r += 1;

  const dataStart = r;
  orderRows.forEach((row) => {
    dc(ws, r, 0, row.card_id       || '');
    dc(ws, r, 1, row.creation_date || '');
    dc(ws, r, 2, row.license_plate || '');
    dc(ws, r, 3, row.tin_number    || '');
    dc(ws, r, 4, row.customer_name || '');
    dc(ws, r, 5, row.product_name  || '');
    dc(ws, r, 6, row.pump_name     || '');
    dc(ws, r, 7, row.volume || 0, { fmt: '#,##0.00' });
    dc(ws, r, 8, row.amount || 0, { fmt: '#,##0' });
    r += 1;
  });
  const dataEnd = r - 1;

  dc(ws, r, 6, 'TOTAL', { fill: TOTAL_FILL });
  if (dataStart <= dataEnd) {
    setFormula(ws, r, 7, `SUM(H${dataStart + 1}:H${dataEnd + 1})`, { fmt: '#,##0.00', fill: TOTAL_FILL });
    setFormula(ws, r, 8, `SUM(I${dataStart + 1}:I${dataEnd + 1})`, { fmt: '#,##0', fill: TOTAL_FILL });
  } else {
    dc(ws, r, 7, 0, { fmt: '#,##0.00', fill: TOTAL_FILL });
    dc(ws, r, 8, 0, { fmt: '#,##0', fill: TOTAL_FILL });
  }
  r += 1;

  return ws;
}

// Fetches order sales for this close report's own date window. Never throws
// - a missing token, a network error, or a non-200 response all just mean
// "no orders data for this sheet", the same outcome as a genuinely empty
// window, so the ORDERS sheet is quietly left out of the workbook rather
// than failing the whole export.
//
// ! That silence has a cost worth remembering: if order_sales_range.py is
// not deployed, this gets a 404 and returns [], and the export succeeds with
// no ORDERS sheet and no warning. A close report that should have orders and
// silently does not is the failure mode to watch for after this deploy.
async function fetchOrderSales(report, token) {
  if (!token || !report.station_serial || !report.start_date || !report.end_date) return [];
  try {
    const params = new URLSearchParams({
      station_serial: report.station_serial,
      start_date:     report.start_date,
      end_date:       report.end_date,
    });
    const res = await fetch(
      `${API_BASE}/ak4tek/cards/order_report/sales_by_range?${params.toString()}`,
      { headers: { Authorization: token } }
    );
    if (!res.ok) return [];
    const json = await res.json().catch(() => ({}));
    return Array.isArray(json.children) ? json.children : [];
  } catch (err) {
    console.warn('Could not fetch order sales for close report Excel:', err);
    return [];
  }
}

// ─── Build workbook ────────────────────────────────────────────────────────────
// `token` is optional - without it (or if the fetch fails) the ORDERS sheet
// is simply skipped, same as a period with zero order sales. Now async
// because filling ORDERS means a network round trip; both call sites
// (CloseReports.js) already run inside an async handler, so this just adds
// an `await`.
export async function buildCloseReportTemplateWorkbook(report, stationName, token) {
  const closingForm = report.closing_form && Object.keys(report.closing_form).length
    ? report.closing_form
    : emptyClosingForm();
  const priceMap = buildPriceMap(report);

  const orderRows = await fetchOrderSales(report, token);

  const wb = XLSX.utils.book_new();

  // Whether each of the three "optional" sheets gets included depends on
  // ORDERS, not just on its own data:
  //   - ORDERS has no rows this period -> show CASH SUMMARY, CREDIT and
  //     CASH DISCOUNT DETAILS regardless, even if they're empty too (the
  //     workbook defaults back to "show everything" when there's nothing
  //     from orders to be selective about).
  //   - ORDERS has rows -> only the non-empty ones among CASH SUMMARY,
  //     CREDIT and CASH DISCOUNT DETAILS are included; an empty one is
  //     left out exactly as before.
  // STOCK REPORT and DELIVERIES are unconditional either way.
  const hasOrders = hasRows(orderRows);

  const hasCashSummaryData =
    hasRows(closingForm.collections) || hasRows(closingForm.cash_expenses) ||
    hasRows(closingForm.fuel_expenses) || (closingForm.opening_cash || 0) > 0;
  if (!hasOrders || hasCashSummaryData) {
    XLSX.utils.book_append_sheet(wb, buildCashSummarySheet(report, stationName, closingForm, priceMap), 'CASH SUMMARY');
  }

  XLSX.utils.book_append_sheet(wb, buildStockReportSheet(report, stationName, closingForm, priceMap), 'STOCK REPORT');

  if (!hasOrders || hasRows(closingForm.credit)) {
    XLSX.utils.book_append_sheet(wb, buildCreditSheet(report, stationName, closingForm, priceMap), 'CREDIT');
  }

  XLSX.utils.book_append_sheet(wb, buildDeliveriesSheet(report, stationName), 'FUEL RECEIVED');

  if (!hasOrders || hasRows(closingForm.cash_discount)) {
    XLSX.utils.book_append_sheet(wb, buildCashDiscountSheet(report, stationName, closingForm, priceMap), 'CASH DISCOUNT DETAILS');
  }

  if (hasOrders) {
    XLSX.utils.book_append_sheet(wb, buildOrdersSheet(report, stationName, orderRows), 'ORDERS');
  }

  // Show long text in full on every sheet (see fitLongText).
  wb.SheetNames.forEach((name) => fitLongText(wb.Sheets[name]));

  return wb;
}

// Convenience: build + trigger a browser download, same naming convention
// as the existing close_report_<station>_<date>.xlsx downloads.
export async function downloadCloseReportTemplate(report, stationName, dateStr, token) {
  const wb       = await buildCloseReportTemplateWorkbook(report, stationName, token);
  const safeName = (stationName || 'station').replace(/[^A-Za-z0-9_-]+/g, '_');
  const filename = `close_report_detail_${safeName}_${dateStr}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}


// ─── DAY VARIATION (fuel shorts), all stations ────────────────────────────────
// The same figures as the stock-movement row of each station's STOCK REPORT
// sheet, worked out here in code instead of Excel formulas, so the
// all-stations summary and the Station Map show exactly what each station's
// own report shows:
//   CLOSING STOCK = OPENING STOCK + RECEIVED - DISPENSED + PUMP TEST
//   DAY VARIATION = ATG - CLOSING STOCK          (minus = fuel short)
// One difference on purpose: a product with no tank in tanks_list gives null
// (shown as "-") instead of the meaningless number the formulas would give.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export function dayVariationOf(report) {
  const closingForm = report && report.closing_form && Object.keys(report.closing_form).length
    ? report.closing_form
    : emptyClosingForm();
  const pumpTestBy = (closingForm.totals || {}).pump_test_by_product || {};
  const pumps = (report && report.pumps_list) || [];
  const tanks = (report && report.tanks_list) || [];

  const out = {};
  ['PETROL', 'DIESEL'].forEach((group) => {
    // All tanks of the product, added up (a station can have two Diesel tanks).
    const groupTanks = tanksOf(tanks, group);
    if (!groupTanks.length) {
      out[group] = null;
      return;
    }
    const opening   = sumTanks(groupTanks, 'StartVolume');
    const received  = sumTanks(groupTanks, 'OrderQty');
    const dispensed = pumps
      .filter((p) => productGroup(p.product) === group)
      .reduce((sum, p) => sum + (Number(p.total_volume_by_sale) || 0), 0);
    const pumpTest  = Number(pumpTestBy[group === 'PETROL' ? 'UNLEADED' : 'DIESEL']) || 0;
    const closing   = opening + received - dispensed + pumpTest;
    const atg       = sumTanks(groupTanks, 'EndVolume');
    out[group] = {
      opening:   round2(opening),
      received:  round2(received),
      dispensed: round2(dispensed),
      pumpTest:  round2(pumpTest),
      closing:   round2(closing),
      atg:       round2(atg),
      variation: round2(atg - closing),
    };
  });
  return out;
}

// station_serial -> that station's close report for `dateStr` (the report
// whose start_date falls on that day - the same day the "Report Date" picker
// on Close Reports means). Several reports for one day: the latest
// generated_at wins, as in findCloseReport. Role scoping is done by the
// server (apply_station_filter).
export async function fetchCloseReportsForDay(token, dateStr, signal) {
  const end = new Date(`${dateStr}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 7); // a report started that day ends within a few days
  const params = new URLSearchParams({
    start_date: `${dateStr}T00:00:00`,
    end_date:   `${end.toISOString().slice(0, 10)}T23:59:59`,
    per_page:   '100',
  });
  const latest = {};
  for (let page = 1; page <= 60; page++) {
    params.set('page', String(page));
    const res = await fetch(`${API_BASE}/ak4tek/close-reports?${params.toString()}`, {
      headers: { Authorization: token },
      signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    const reports = body.reports || [];
    reports.forEach((r) => {
      if (!r.station_serial || (r.start_date || '').slice(0, 10) !== dateStr) return;
      const prev = latest[r.station_serial];
      if (!prev || (r.generated_at || '') > (prev.generated_at || '')) latest[r.station_serial] = r;
    });
    if (reports.length === 0 || !body.pagination || !body.pagination.has_next) break;
  }
  return latest;
}

const VAR_FMT   = '+#,##0.00;-#,##0.00;0.00';
const SHORT_FONT = { name: 'Arial', sz: 10, bold: true, color: { rgb: 'C00000' } }; // red - fuel short
const GAIN_FONT  = { name: 'Arial', sz: 10, bold: true, color: { rgb: '006100' } }; // green - gain
const varFont = (v) => (v < 0 ? SHORT_FONT : v > 0 ? GAIN_FONT : DATA_FONT);

// rows: [{ stationName, report }] - report is null when the station has no
// close report for that day. One sheet, one row per station, in the order
// given, then a TOTAL row.
export function buildDayVariationWorkbook(rows, dateStr) {
  const ws = {};
  const LAST = 8; // A..I
  setColWidths(ws, [6, 30, 15, 15, 16, 15, 15, 16, 34]);

  let r = 0;
  mergeCells(ws, r, 0, r, LAST);
  dc(ws, r, 0, 'DAY VARIATION (FUEL SHORTS) - ALL STATIONS', { font: TITLE_FONT });
  setRowHeight(ws, r, 24);
  r += 1;
  mergeCells(ws, r, 0, r, LAST);
  dc(ws, r, 0, `Close reports of ${dateStr}  -  DAY VARIATION = ATG - CLOSING STOCK (minus = short)`, { font: GROUP_FONT });
  r += 2;

  dc(ws, r, 0, '', { fill: HEADER_FILL });
  dc(ws, r, 1, '', { fill: HEADER_FILL });
  groupTitle(ws, r, 2, 4, 'PETROL');
  groupTitle(ws, r, 5, 7, 'DIESEL');
  dc(ws, r, 8, '', { fill: HEADER_FILL });
  r += 1;
  ['SN', 'STATION', 'CLOSING STOCK', 'ATG', 'DAY VARIATION', 'CLOSING STOCK', 'ATG', 'DAY VARIATION', 'PERIOD / NOTE']
    .forEach((h, c) => dc(ws, r, c, h, { font: HEADER_FONT, fill: c === 4 || c === 7 ? GRAND_FILL : HEADER_FILL }));
  setRowHeight(ws, r, 18);
  r += 1;

  const first = r;
  rows.forEach((row, i) => {
    const v = row.report ? dayVariationOf(row.report) : null;
    dc(ws, r, 0, i + 1);
    dc(ws, r, 1, row.stationName || '');
    [['PETROL', 2], ['DIESEL', 5]].forEach(([group, c]) => {
      const g = v && v[group];
      if (!g) {
        dc(ws, r, c, '-'); dc(ws, r, c + 1, '-'); dc(ws, r, c + 2, '-');
        return;
      }
      const col = (k) => XLSX.utils.encode_col(k);
      dc(ws, r, c, g.closing, { fmt: '#,##0.00' });
      dc(ws, r, c + 1, g.atg, { fmt: '#,##0.00' });
      setFormula(ws, r, c + 2, `${col(c + 1)}${r + 1}-${col(c)}${r + 1}`, { fmt: VAR_FMT, font: varFont(g.variation) });
      ws[XLSX.utils.encode_cell({ r, c: c + 2 })].v = g.variation; // shown before Excel recalculates
    });
    const rep = row.report;
    dc(ws, r, 8, rep
      ? `${(rep.start_date || '').slice(0, 16).replace('T', ' ')} to ${(rep.end_date || '').slice(0, 16).replace('T', ' ')}`
      : 'No close report for this day');
    r += 1;
  });
  const last = r - 1;

  dc(ws, r, 0, '', { fill: GRAND_FILL, border: TOTAL_BORDER });
  dc(ws, r, 1, 'TOTAL', { fill: GRAND_FILL, border: TOTAL_BORDER });
  for (let c = 2; c <= 7; c++) {
    const col = XLSX.utils.encode_col(c);
    if (last >= first) {
      setFormula(ws, r, c, `SUM(${col}${first + 1}:${col}${last + 1})`, {
        fmt: c === 4 || c === 7 ? VAR_FMT : '#,##0.00', fill: GRAND_FILL, border: TOTAL_BORDER,
      });
    } else {
      dc(ws, r, c, 0, { fill: GRAND_FILL, border: TOTAL_BORDER });
    }
  }
  dc(ws, r, 8, '', { fill: GRAND_FILL, border: TOTAL_BORDER });

  fitLongText(ws);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'DAY VARIATION');
  return wb;
}

export function downloadDayVariation(rows, dateStr) {
  const filename = `day_variation_all_stations_${dateStr}.xlsx`;
  XLSX.writeFile(buildDayVariationWorkbook(rows, dateStr), filename);
  return filename;
}